/**
 * De Novo Generation Shortcut — "generate new compounds", "run REINVENT"
 *
 * Hit discovery path 3: AI-driven de novo molecular generation using
 * REINVENT 4. Generates novel compounds optimized for drug-likeness,
 * synthetic accessibility, and target properties via reinforcement
 * learning.
 *
 * This is a job-based workflow — it submits to the Temporal job
 * orchestrator and runs asynchronously on GPU workers.
 *
 * @module gibbs/knowledge/deNovoGenerationShortcut
 */

// Keep TIGHT — canonical phrases only. The LLM handles fuzzy variants.
const DE_NOVO_PHRASES = [
    'de novo',
    'denovo',
    'generate compounds',
    'generate molecules',
    'generate new compounds',
    'generate new molecules',
    'run reinvent',
    'reinvent',
    'generative design',
];

/**
 * @param {string} userMessage
 * @returns {boolean}
 */
export function matchDeNovoIntent(userMessage) {
    if (!userMessage || typeof userMessage !== 'string') return false;
    const lower = userMessage.toLowerCase();
    return DE_NOVO_PHRASES.some((p) => lower.includes(p));
}

// ─── Precondition check ────────────────────────────────────────────────────

/**
 * Check workspace preconditions for de novo generation.
 *
 * @param {Object} workspace - App.Workspace
 * @returns {{ready: boolean, message?: string}}
 */
export function checkDeNovoPreconditions(workspace) {
    if (!workspace) {
        return { ready: false, message: 'No workspace available.' };
    }

    // Compound loaded? (REINVENT needs a seed molecule)
    if (typeof workspace.getLoadedCompounds === 'function') {
        const compounds = workspace.getLoadedCompounds();
        if (!Array.isArray(compounds) || compounds.length === 0) {
            return {
                ready: false,
                message: 'I understand you want to run **de novo generation**. '
                    + 'However, REINVENT needs at least one **seed compound** '
                    + 'as a starting point for molecular generation. Load or '
                    + 'draw a compound first, then ask me again.',
            };
        }
    }

    // Jobman available?
    if (workspace.isJobmanAvailable?.() === false) {
        return {
            ready: false,
            message: 'I understand you want to run **de novo generation**. '
                + 'However, the job manager service is not available. '
                + 'REINVENT requires the GPU job orchestrator to be '
                + 'running. Contact your system administrator.',
        };
    }

    return { ready: true };
}

// ─── Workflow builder ──────────────────────────────────────────────────────

/**
 * Build the de novo generation workflow.
 *
 * Pipeline:
 *   1. job-reinvent — generate novel compounds from the active
 *      compound as seed, optimizing for drug-likeness and binding
 *
 * Results: a table of generated molecules with scores (QED, SA,
 * MW, logP, etc.) that can be loaded into the workspace for
 * further evaluation.
 *
 * @returns {Object} workflow-intent plan
 */
export function buildDeNovoWorkflow() {
    return {
        intent: 'workflow',
        summary: 'De novo molecular generation with REINVENT: '
            + 'use the active compound as a seed to generate novel '
            + 'drug-like molecules optimized for binding affinity, '
            + 'drug-likeness (QED), and synthetic accessibility. '
            + 'Results will appear as new compounds when the job '
            + 'completes.',
        actions: [
            {
                actionId: 'job-reinvent',
                params: {},
                resolvedInputs: {
                    smilesList: 'active',
                },
                step: 1,
            },
        ],
    };
}

/**
 * Top-level entry point for the de novo generation shortcut.
 *
 * @param {string} userMessage
 * @param {Object} workspaceState
 * @returns {Object|null} workflow intent, chat message, or null
 */
export function tryDeNovoShortcut(userMessage, workspaceState) {
    if (!matchDeNovoIntent(userMessage)) return null;

    const check = checkDeNovoPreconditions(workspaceState);
    if (!check.ready) {
        return {
            intent: 'chat',
            message: check.message,
        };
    }

    return buildDeNovoWorkflow();
}
